import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Gesture, GestureController, ModalController } from '@ionic/angular';
import { ModalOptions } from '@ionic/core';
import { Capacitor } from '@capacitor/core';
import { iosLeaveAnimation } from '@core/services/modal/animations/ios.leave';
import { SwipeToCloseGestureService } from '@core/services/modal/gestures/swipe-to-close-gesture.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    private swipeToCloseGesture: Gesture;

    constructor(
        private gestureController: GestureController,
        private modalController: ModalController,
        private swipeToCloseGestureService: SwipeToCloseGestureService,
        router: Router
    ) {
        // Red de seguridad: los modales pueden cerrarse sin pasar por dismiss()
        // (closeAllModals, gesto, modalController directo) y las llamadas al
        // bridge nativo pueden ejecutarse fuera de orden, dejando la status bar
        // oscura (en Android 15+ eso son iconos blancos sobre fondo blanco).
        // Se restaura cuando ya no queda ningun modal abierto.
        if (Capacitor.isNativePlatform()) {
            document.addEventListener('ionModalDidDismiss', () => this.restoreStatusBarIfNoModal());
            router.events
                .pipe(filter(event => event instanceof NavigationEnd))
                .subscribe(() => this.restoreStatusBarIfNoModal());
        }
    }

    async create(options: ModalOptions): Promise<HTMLIonModalElement> {
        const swipeToClose = options.canDismiss;
        const routerOutlet = document.querySelector('ion-router-outlet');

        // Remove iOS modal style for Android devices
        if (Capacitor.getPlatform() !== 'android') {
            // Prevent doing Ionic gesture, we handle it later
            options = {
                presentingElement: await this.modalController.getTop() ?? routerOutlet,
                ...options,
            };
        }

        options = {
            backdropDismiss: false,
            ...options,
            canDismiss: (data?: any, role?: string) => {
                return new Promise<boolean>(resolve => resolve(!!swipeToClose || role === 'close' || role === 'cancel' || role === 'save'))
              },
        };

        if (Capacitor.isNativePlatform()) {
            this.setStatusBarColor('#000000', Style.Dark);
        }

        const modal = await this.modalController.create(options);

        if (swipeToClose) {
            const leaveAnimation = iosLeaveAnimation(modal, modal.presentingElement);

            modal.addEventListener('ionModalDidPresent', () => {
                this.swipeToCloseGesture = this.swipeToCloseGestureService.createGesture(
                    this.gestureController,
                    modal,
                    leaveAnimation,
                    () => {
                        leaveAnimation.onFinish(async () => {
                            await this.dismiss(undefined, 'gesture', options.id);
                        });
                    }
                );
                this.swipeToCloseGesture.enable(true);
            });
        }

        return modal;
    }

    dismiss(data?: any, role?: string, id?: string): Promise<boolean> {
        // set style back to normal
        if (Capacitor.isNativePlatform()) {
            this.setStatusBarColor('#FFFFFF', Style.Light);
        }
        this.swipeToCloseGesture?.enable(false);

        let finalRole = role;

        if (role === undefined || role === '') {
            finalRole = 'close';
        }

        return this.modalController.dismiss(data, finalRole, id);
    }

    getTop(): Promise<HTMLIonModalElement> {
        return this.modalController.getTop();
    }

    private async restoreStatusBarIfNoModal(): Promise<void> {
        const top = await this.modalController.getTop();
        if (!top) {
            this.setStatusBarColor('#FFFFFF', Style.Light);
        }
    }

    private setStatusBarColor(color: string, style: Style): void {
        StatusBar.setBackgroundColor({ color }).catch(() => {});
        StatusBar.setStyle({ style }).catch(() => {});

        // En Android 15+ StatusBar.setBackgroundColor es no-op (edge-to-edge
        // forzado): ahí el fondo lo pinta el plugin EdgeToEdge
        if (Capacitor.getPlatform() === 'android') {
            EdgeToEdge.setStatusBarColor({ color }).catch(() => {});
        }
    }

    async closeAllModals() {
        const modal = await this.modalController.getTop();
        if (modal) {
            await modal.dismiss(undefined, "close");
            this.closeAllModals();
        }
    }

    async closeTopModal(): Promise<void> {
        try {
            const element = await this.modalController.getTop();
            element?.dismiss(undefined, "close");
        } catch (error) {
        }
    }
}
